import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Globe, DollarSign, Hash, Loader2, Calendar } from 'lucide-react';
import '../../styles/erp-theme.css';
import { API_BASE, API } from '../../lib/api';


// ======================== CONSTANTS ========================

// Grouped for the Globalization selector: India (primary 5) -> India (Others) -> International (top 5)
const LANGUAGES = [
  // India — primary (English default, then Hindi, Telugu, Tamil, Kannada)
  { code: 'en-IN', label: 'English (India)', sarvam: null, group: 'india' },
  { code: 'hi-IN', label: 'Hindi', sarvam: 'hi-IN', group: 'india' },
  { code: 'te-IN', label: 'Telugu', sarvam: 'te-IN', group: 'india' },
  { code: 'ta-IN', label: 'Tamil', sarvam: 'ta-IN', group: 'india' },
  { code: 'kn-IN', label: 'Kannada', sarvam: 'kn-IN', group: 'india' },
  // India — others
  { code: 'bn-IN', label: 'Bengali', sarvam: 'bn-IN', group: 'india-other' },
  { code: 'mr-IN', label: 'Marathi', sarvam: 'mr-IN', group: 'india-other' },
  { code: 'gu-IN', label: 'Gujarati', sarvam: 'gu-IN', group: 'india-other' },
  { code: 'ml-IN', label: 'Malayalam', sarvam: 'ml-IN', group: 'india-other' },
  { code: 'pa-IN', label: 'Punjabi', sarvam: 'pa-IN', group: 'india-other' },
  // International — top 5
  { code: 'ar-SA', label: 'Arabic', sarvam: 'ar-SA', group: 'international' },
  { code: 'fr-FR', label: 'French', sarvam: 'fr-FR', group: 'international' },
  { code: 'es-ES', label: 'Spanish', sarvam: null, group: 'international' },
  { code: 'de-DE', label: 'German', sarvam: 'de-DE', group: 'international' },
  { code: 'zh-CN', label: 'Chinese', sarvam: null, group: 'international' },
  { code: 'en-US', label: 'English (US)', sarvam: null, group: 'international' },
];

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee' },
  { code: 'EUR', symbol: '\u20AC', name: 'Euro' },
  { code: 'AED', symbol: '\u062F.\u0625', name: 'UAE Dirham' },
  { code: 'GBP', symbol: '\u00A3', name: 'British Pound' },
  { code: 'JPY', symbol: '\u00A5', name: 'Japanese Yen' },
  { code: 'SAR', symbol: '\uFDFC', name: 'Saudi Riyal' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
];

const NUMBER_FORMATS = [
  { code: 'en-IN', label: 'India', flag: '\uD83C\uDDEE\uD83C\uDDF3', example: '1,00,000.00', style: 'lakh' },
  { code: 'en-US', label: 'USA', flag: '\uD83C\uDDFA\uD83C\uDDF8', example: '100,000.00', style: 'standard' },
  { code: 'de-DE', label: 'Europe', flag: '\uD83C\uDDEA\uD83C\uDDFA', example: '100.000,00', style: 'europe' },
  { code: 'ar-SA', label: 'Middle East', flag: '\uD83C\uDDF8\uD83C\uDDE6', example: '100,000.00', style: 'standard' },
  { code: 'ja-JP', label: 'Japan', flag: '\uD83C\uDDEF\uD83C\uDDF5', example: '100000', style: 'plain' },
];

const DATE_FORMATS = [
  { code: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '21/03/2026', countries: ['en-IN', 'hi-IN', 'fr-FR', 'de-DE', 'ta-IN', 'te-IN', 'bn-IN', 'mr-IN', 'gu-IN', 'kn-IN', 'ml-IN', 'pa-IN'] },
  { code: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '03/21/2026', countries: ['en-US'] },
  { code: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-03-21', countries: ['ja-JP'] },
  { code: 'DD-MM-YYYY', label: 'DD-MM-YYYY', example: '21-03-2026', countries: [] },
  { code: 'DD.MM.YYYY', label: 'DD.MM.YYYY', example: '21.03.2026', countries: [] },
  { code: 'YYYY/MM/DD', label: 'YYYY/MM/DD', example: '2026/03/21', countries: [] },
  { code: 'DD MMM YYYY', label: 'DD MMM YYYY', example: '21 Mar 2026', countries: ['ar-SA'] },
];

// Country → default date format mapping
const COUNTRY_DATE_MAP = {};
DATE_FORMATS.forEach(df => { df.countries.forEach(c => { COUNTRY_DATE_MAP[c] = df.code; }); });

// ======================== DEFAULT CONFIG ========================

export const DEFAULT_GLOBALIZATION = {
  currency: { baseCurrency: 'INR', displayCurrency: 'INR', exchangeRateSource: 'manual', rate: 1 },
  language: 'en-IN',
  numberFormat: { locale: 'en-IN', style: 'lakh', decimalPlaces: 2 },
  dateFormat: 'DD/MM/YYYY'
};

// ======================== FORMAT ENGINE ========================

const translationCache = {};

function getCurrencySymbol(currencyCode) {
  const c = CURRENCIES.find(c => c.code === currencyCode);
  return c ? c.symbol : currencyCode;
}

function convertCurrency(value, fromRate, toRate) {
  if (!value || isNaN(value) || !fromRate || !toRate) return value;
  return (value / fromRate) * toRate;
}

function formatNumber(value, locale, decimalPlaces, style) {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return String(value);
  try {
    if (style === 'plain') return num.toFixed(decimalPlaces);
    return new Intl.NumberFormat(locale, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces }).format(num);
  } catch { return num.toFixed(decimalPlaces); }
}

function isMonetaryColumn(colName) {
  const monetary = ['amount','price','cost','revenue','total','tax','discount','balance','payment','salary','wage','rate','value','money','charge','fee','gross','net','debit','credit','bill','invoice','profit','loss','margin','receipt','deposit','withdraw','commission','bonus','penalty','refund','netamt','grossamt','taxamt','disamt','saleprice','costprice','mrp','amt','taxable','totamt','subtotal','grandtotal','roundoff','invtotal','pitotal','pototal','receivedcash'];
  const lower = colName.toLowerCase();
  return monetary.some(m => lower.includes(m));
}

function isNumericValue(val) {
  if (typeof val === 'number') return true;
  if (typeof val === 'string') return !isNaN(parseFloat(val)) && isFinite(val);
  return false;
}

function isDateColumn(colName) {
  const datePatterns = ['date','dt','dob','joining','created_at','updated_at','invdate','pidate','podate','birthdate','expiry','expdate','startdate','enddate','fromdate','todate'];
  const lower = colName.toLowerCase();
  return datePatterns.some(p => lower.includes(p));
}

function isDateValue(val) {
  if (!val || typeof val !== 'string') return false;
  // ISO date or common date patterns
  if (/^\d{4}-\d{2}-\d{2}/.test(val)) return true;
  if (/^\d{2}[\/\-\.]\d{2}[\/\-\.]\d{4}/.test(val)) return true;
  const d = new Date(val);
  return !isNaN(d.getTime()) && val.length >= 8 && val.length <= 30;
}

export function formatDate(val, format) {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const mmm = months[d.getMonth()];
  switch (format) {
    case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`;
    case 'MM/DD/YYYY': return `${mm}/${dd}/${yyyy}`;
    case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
    case 'DD-MM-YYYY': return `${dd}-${mm}-${yyyy}`;
    case 'DD.MM.YYYY': return `${dd}.${mm}.${yyyy}`;
    case 'YYYY/MM/DD': return `${yyyy}/${mm}/${dd}`;
    case 'DD MMM YYYY': return `${dd} ${mmm} ${yyyy}`;
    default: return `${dd}/${mm}/${yyyy}`;
  }
}

// Main transformation function
export function applyGlobalization(row, config, exchangeRates, translatedLabels = {}) {
  if (!config || !row) return row;
  const result = {};
  const { currency, numberFormat, dateFormat: dateFmt } = config;
  const baseRate = exchangeRates?.[currency.baseCurrency] || 1;
  const displayRate = exchangeRates?.[currency.displayCurrency] || 1;
  const sym = getCurrencySymbol(currency.displayCurrency);
  const manualRate = currency.exchangeRateSource === 'manual' ? (currency.rate || 1) : null;

  for (const [key, value] of Object.entries(row)) {
    const displayKey = translatedLabels[key] || key;
    if (isDateColumn(key) && isDateValue(value)) {
      result[displayKey] = formatDate(value, dateFmt || 'DD/MM/YYYY');
    } else if (isMonetaryColumn(key) && isNumericValue(value)) {
      let converted;
      if (manualRate && currency.baseCurrency !== currency.displayCurrency) {
        converted = parseFloat(value) * manualRate;
      } else if (currency.baseCurrency !== currency.displayCurrency) {
        converted = convertCurrency(parseFloat(value), baseRate, displayRate);
      } else {
        converted = parseFloat(value);
      }
      result[displayKey] = `${sym} ${formatNumber(converted, numberFormat.locale, numberFormat.decimalPlaces, numberFormat.style)}`;
    } else if (isNumericValue(value) && typeof value === 'number') {
      result[displayKey] = formatNumber(value, numberFormat.locale, numberFormat.decimalPlaces, numberFormat.style);
    } else {
      result[displayKey] = value;
    }
  }
  return result;
}

export function applyGlobalizationToDataset(data, config, exchangeRates, translatedLabels = {}) {
  if (!data?.length || !config) return data;
  return data.map(row => applyGlobalization(row, config, exchangeRates, translatedLabels));
}

export function getChartData(data, config, exchangeRates) {
  if (!data?.length || !config) return data;
  const { currency } = config;
  const baseRate = exchangeRates?.[currency.baseCurrency] || 1;
  const displayRate = exchangeRates?.[currency.displayCurrency] || 1;
  const manualRate = currency.exchangeRateSource === 'manual' ? (currency.rate || 1) : null;

  return data.map(row => {
    const result = {};
    for (const [key, value] of Object.entries(row)) {
      if (isMonetaryColumn(key) && isNumericValue(value)) {
        if (manualRate && currency.baseCurrency !== currency.displayCurrency) {
          result[key] = parseFloat(value) * manualRate;
        } else if (currency.baseCurrency !== currency.displayCurrency) {
          result[key] = convertCurrency(parseFloat(value), baseRate, displayRate);
        } else {
          result[key] = parseFloat(value);
        }
      } else {
        result[key] = value;
      }
    }
    return result;
  });
}

// ======================== GLOBALIZATION BAR COMPONENT ========================

export const GlobalizationBar = ({ config, onChange, token }) => {
  const [exchangeRates, setExchangeRates] = useState({});

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await fetch(`${API}/globalization/exchange-rates`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setExchangeRates(d.rates || {}); }
      } catch {}
    };
    fetchRates();
  }, [token]);

  const updateCurrency = (field, value) => {
    const newCurrency = { ...config.currency, [field]: value };
    if (field === 'displayCurrency' && newCurrency.exchangeRateSource === 'manual') {
      const baseRate = exchangeRates[newCurrency.baseCurrency] || 1;
      const targetRate = exchangeRates[value] || 1;
      newCurrency.rate = Math.round((targetRate / baseRate) * 10000) / 10000;
    }
    onChange({ ...config, currency: newCurrency });
  };

  const updateNumberFormat = (field, value) => {
    const fmt = { ...config.numberFormat, [field]: value };
    if (field === 'locale') {
      const nf = NUMBER_FORMATS.find(n => n.code === value);
      if (nf) fmt.style = nf.style;
    }
    onChange({ ...config, numberFormat: fmt });
  };

  const updateLanguage = (lang) => {
    const updates = { ...config, language: lang };
    // Auto-set date format based on language/country
    if (COUNTRY_DATE_MAP[lang]) {
      updates.dateFormat = COUNTRY_DATE_MAP[lang];
    }
    onChange(updates);
  };

  return (
    <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 border-b bg-muted/20 shrink-0 flex-wrap" data-testid="globalization-bar">
      {/* Language */}
      <div className="flex items-center gap-1" data-testid="language-selector">
        <Globe className="w-3.5 h-3.5 text-blue-500 shrink-0" />
        <Select value={config.language} onValueChange={updateLanguage}>
          <SelectTrigger className="h-6 text-[10px] w-[120px] border-blue-200" data-testid="language-select">
            <SelectValue placeholder="Language" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {LANGUAGES.filter(l => l.group === 'india').map(l => (<SelectItem key={l.code} value={l.code} className="text-xs">{l.label}</SelectItem>))}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel className="text-[10px] text-muted-foreground">Others (India)</SelectLabel>
              {LANGUAGES.filter(l => l.group === 'india-other').map(l => (<SelectItem key={l.code} value={l.code} className="text-xs">{l.label}</SelectItem>))}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel className="text-[10px] text-muted-foreground">International</SelectLabel>
              {LANGUAGES.filter(l => l.group === 'international').map(l => (<SelectItem key={l.code} value={l.code} className="text-xs">{l.label}</SelectItem>))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="w-px h-5 bg-border" />

      {/* Currency */}
      <div className="flex items-center gap-1" data-testid="currency-selector">
        <DollarSign className="w-3.5 h-3.5 text-green-500 shrink-0" />
        <Select value={config.currency.displayCurrency} onValueChange={(v) => updateCurrency('displayCurrency', v)}>
          <SelectTrigger className="h-6 text-[10px] w-[110px] border-green-200" data-testid="currency-select">
            <SelectValue placeholder="Currency" />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map(c => (<SelectItem key={c.code} value={c.code} className="text-xs">{c.symbol} {c.code}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-0.5">
          <span className="text-[8px] text-muted-foreground">{config.currency.exchangeRateSource === 'manual' ? 'M' : 'A'}</span>
          <Switch checked={config.currency.exchangeRateSource === 'manual'} onCheckedChange={(v) => updateCurrency('exchangeRateSource', v ? 'manual' : 'live')} className="h-3 w-6" data-testid="rate-mode-toggle" />
        </div>
        {config.currency.exchangeRateSource === 'manual' && config.currency.baseCurrency !== config.currency.displayCurrency && (
          <Input type="number" step="0.0001" value={config.currency.rate} onChange={(e) => updateCurrency('rate', parseFloat(e.target.value) || 1)} className="h-6 w-[60px] text-[10px]" data-testid="manual-rate-input" />
        )}
      </div>

      <div className="w-px h-5 bg-border" />

      {/* Number Format */}
      <div className="flex items-center gap-1" data-testid="number-format-selector">
        <Hash className="w-3.5 h-3.5 text-orange-500 shrink-0" />
        <Select value={config.numberFormat.locale} onValueChange={(v) => updateNumberFormat('locale', v)}>
          <SelectTrigger className="h-6 text-[10px] w-[100px] border-orange-200" data-testid="number-format-select">
            <SelectValue placeholder="Format" />
          </SelectTrigger>
          <SelectContent>
            {NUMBER_FORMATS.map(n => (<SelectItem key={n.code} value={n.code} className="text-xs">{n.flag} {n.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={String(config.numberFormat.decimalPlaces)} onValueChange={(v) => updateNumberFormat('decimalPlaces', parseInt(v))}>
          <SelectTrigger className="h-6 text-[10px] w-[38px]" data-testid="decimal-places-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[0,1,2,3,4].map(n => (<SelectItem key={n} value={String(n)} className="text-xs">{n}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-px h-5 bg-border" />

      {/* Date Format */}
      <div className="flex items-center gap-1" data-testid="date-format-selector">
        <Calendar className="w-3.5 h-3.5 text-purple-500 shrink-0" />
        <Select value={config.dateFormat || 'DD/MM/YYYY'} onValueChange={(v) => onChange({ ...config, dateFormat: v })}>
          <SelectTrigger className="h-6 text-[10px] w-[110px] border-purple-200" data-testid="date-format-select">
            <SelectValue placeholder="Date" />
          </SelectTrigger>
          <SelectContent>
            {DATE_FORMATS.map(d => (<SelectItem key={d.code} value={d.code} className="text-xs">{d.label} ({d.example})</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Conversion badge */}
      {config.currency.baseCurrency !== config.currency.displayCurrency && (
        <Badge variant="outline" className="text-[8px] h-4 ml-auto border-green-300 text-green-600" data-testid="conversion-badge">
          {config.currency.baseCurrency} {'\u2192'} {config.currency.displayCurrency}
          {config.currency.exchangeRateSource === 'manual' ? ` @${config.currency.rate}` : ' (auto)'}
        </Badge>
      )}
    </div>
  );
};

// ======================== HOOK FOR TRANSLATION ========================

export function useTranslation(token, language, columnNames) {
  const [translatedLabels, setTranslatedLabels] = useState({});
  const [loading, setLoading] = useState(false);
  const prevLangRef = useRef('');
  const prevColsRef = useRef('');

  const translate = useCallback(async () => {
    const langDef = LANGUAGES.find(l => l.code === language);
    if (!langDef?.sarvam || !columnNames?.length) { setTranslatedLabels({}); return; }
    const colKey = columnNames.sort().join(',');
    const cacheKey = `${language}::${colKey}`;
    if (translationCache[cacheKey]) { setTranslatedLabels(translationCache[cacheKey]); return; }
    if (prevLangRef.current === language && prevColsRef.current === colKey) return;
    prevLangRef.current = language;
    prevColsRef.current = colKey;
    setLoading(true);
    try {
      const res = await fetch(`${API}/globalization/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ texts: columnNames, targetLanguage: langDef.sarvam, sourceLanguage: 'en-IN' })
      });
      if (res.ok) {
        const d = await res.json();
        const map = {};
        (d.translations || []).forEach(t => { map[t.original] = t.translated; });
        translationCache[cacheKey] = map;
        setTranslatedLabels(map);
      }
    } catch {}
    finally { setLoading(false); }
  }, [token, language, columnNames]);

  useEffect(() => { translate(); }, [translate]);
  return { translatedLabels, loading };
}

export default GlobalizationBar;
